import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Label, Button, Icon } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import AddressAutocomplete from '../formBuilderAnswersComponents/addressComponents/addressAutocomplete';
import { countryOptions } from '../../constants/countries';

export interface CityLocationValue {
  city: string;
  state: string;
  country: string; // ISO 3166-1 alpha-2 (uppercase)
}

interface Props {
  idPrefix: string;
  value: CityLocationValue;
  onChange: (next: CityLocationValue) => void;
}

// Country dropdown options come pre-built with lowercase keys for the `flag`
// CSS class. We render with the existing options but normalise the stored
// value to uppercase so the API always receives the ISO alpha-2 code.
const upperCountryOptions = countryOptions.map((o) => ({
  ...o,
  value: String(o.value).toUpperCase(),
}));

const isComplete = (v: CityLocationValue) =>
  Boolean(v.city) && Boolean(v.state) && Boolean(v.country);

const CityLocationInput: React.FC<Props> = ({ idPrefix, value, onChange }) => {
  const [mode, setMode] = useState<'autocomplete' | 'manual'>(
    isComplete(value) ? 'autocomplete' : 'manual'
  );
  // In autocomplete mode, if a complete value is already saved we render a
  // chip + Change button instead of the search input. `isSearching` flips true
  // when the user clicks Change, and back to false once a new suggestion is
  // picked (or the parent value is otherwise complete).
  const [isSearching, setIsSearching] = useState<boolean>(!isComplete(value));
  const [searchValue, setSearchValue] = useState<{ searchString: string }>({
    searchString: value.city || '',
  });

  // Track whether the user has explicitly toggled mode. Once they have, we
  // respect their choice and stop deriving mode from `value`.
  const userToggled = useRef(false);

  // If `value` hydrates asynchronously after mount (e.g. parent loads an
  // existing record into state), re-derive the default mode so we land in the
  // chip+Change view when a complete value arrives. Skip once the user has
  // toggled manually to avoid yanking them out of their chosen mode.
  useEffect(() => {
    if (userToggled.current) return;
    if (isComplete(value)) {
      setMode('autocomplete');
      setIsSearching(false);
    } else {
      setMode('manual');
    }
  }, [value]);

  const countriesForSelect = useMemo(() => upperCountryOptions, []);

  const handleAutocompleteChange = (next: any) => {
    if (typeof next?.searchString === 'string') {
      setSearchValue({ searchString: next.searchString });
    }
    const hasResolvedAddress = next && (next.suburb || next.colloquial_suburb || next.country || next.state);
    if (!hasResolvedAddress) return;

    const city = (next.suburb || next.colloquial_suburb || '').toString();
    const state = (next.state || '').toString();
    const country = (next.country || '').toString().toUpperCase();
    onChange({ city, state, country });
    setIsSearching(false);
  };

  const switchToManual = () => {
    userToggled.current = true;
    setMode('manual');
  };
  const switchToAutocomplete = () => {
    userToggled.current = true;
    setSearchValue({ searchString: value.city || '' });
    setIsSearching(!isComplete(value));
    setMode('autocomplete');
  };
  const startChanging = () => {
    setSearchValue({ searchString: value.city || '' });
    setIsSearching(true);
  };
  const cancelChanging = () => setIsSearching(false);

  const showChip = mode === 'autocomplete' && isComplete(value) && !isSearching;

  return (
    <div>
      {mode === 'autocomplete' ? (
        <Form.Field>
          <label><Translate value='common.location.searchLabel' /></label>
          {showChip ? (
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
              <Label
                basic
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0 1em',
                  fontSize: '1em',
                  fontWeight: 'normal',
                  margin: 0,
                }}
              >
                <Icon name='map marker alternate' />
                {`${value.city}, ${value.state}, ${value.country}`}
              </Label>
              <Button type='button' basic onClick={startChanging} style={{ margin: 0 }}>
                <Translate value='common.location.change' />
              </Button>
            </div>
          ) : (
            <>
              <AddressAutocomplete
                allowFilteringByCities
                value={searchValue}
                onChange={handleAutocompleteChange}
              />
              {isComplete(value) && (
                <Button
                  type='button'
                  basic
                  onClick={cancelChanging}
                  style={{ marginTop: 8 }}
                >
                  <Translate value='common.location.cancelChange' />
                </Button>
              )}
            </>
          )}
          <div style={{ marginTop: 8 }}>
            <Button
              type='button'
              basic
              compact
              size='tiny'
              onClick={switchToManual}
              className='link-style-button'
              style={{
                background: 'none',
                boxShadow: 'none',
                padding: 0,
                color: 'var(--link-color, #4183c4)',
                textDecoration: 'underline',
              }}
            >
              <Translate value='common.location.switchToManual' />
            </Button>
          </div>
        </Form.Field>
      ) : (
        <>
          <Form.Group widths='equal' style={{ marginBottom: 0 }}>
            <Form.Field>
              <label><Translate value='common.location.city' /></label>
              <input
                id={`${idPrefix}-city`}
                type='text'
                value={value.city || ''}
                onChange={(e) => onChange({ ...value, city: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label><Translate value='common.location.state' /></label>
              <input
                id={`${idPrefix}-state`}
                type='text'
                value={value.state || ''}
                onChange={(e) => onChange({ ...value, state: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label><Translate value='common.location.country' /></label>
              <Form.Select
                id={`${idPrefix}-country`}
                fluid
                search
                clearable
                placeholder={I18n.t('common.location.countryPlaceholder')}
                value={(value.country || '').toUpperCase()}
                options={countriesForSelect}
                onChange={(_e, { value: next }) =>
                  onChange({ ...value, country: String(next || '').toUpperCase() })
                }
              />
            </Form.Field>
          </Form.Group>
          <div style={{ marginTop: 5, marginBottom: 16 }}>
            <Button
              type='button'
              basic
              compact
              size='tiny'
              onClick={switchToAutocomplete}
              className='link-style-button'
              style={{
                background: 'none',
                boxShadow: 'none',
                padding: 0,
                color: 'var(--link-color, #4183c4)',
                textDecoration: 'underline',
              }}
            >
              <Translate value='common.location.switchToAutocomplete' />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CityLocationInput;
